import React, { useState, useEffect } from 'react';
import { Calendar, Play, Pause, Trash2, Eye, Loader2, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { communicationService } from '../../../services/communicationService';
import CampaignModal from './CampaignModal';
import ExecutionTracking from './ExecutionTracking';

const CampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [executingCampaignId, setExecutingCampaignId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadCampaigns();
  }, [currentPage, pageSize]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const result = await communicationService.getAllCampaigns({ page: currentPage, pageSize: pageSize });
      setCampaigns(result.data || []);
      setTotalCount(result.total || 0);
      setTotalPages(Math.ceil((result.total || 0) / pageSize));
    } catch (error) {
      console.error('Error loading campaigns:', error);
      alert('Failed to load campaigns: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleCreateCampaign = () => {
    setEditingCampaign(null);
    setIsModalOpen(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setIsModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;

    try {
      await communicationService.deleteCampaign(campaignId);
      await loadCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign: ' + error.message);
    }
  };

  const handleSaveCampaign = async (campaignData, campaignId) => {
    if (campaignId) {
      await communicationService.updateCampaign(campaignId, {
        name: campaignData.name,
        targeted_provider_message_template_ids: campaignData.templateIds,
        communication_campaign_type: campaignData.campaignType,
        communication_campaign_desc: campaignData.description,
        recipient_criteria: campaignData.recipientCriteria,
        scheduled_at: campaignData.scheduledAt
      });
    } else {
      await communicationService.createCampaign(campaignData);
    }
    await loadCampaigns();
  };

  const handleExecuteCampaign = async (campaignId) => {
    if (!confirm('Are you sure you want to execute this campaign? This will send messages to all matching recipients.')) return;

    setExecutingCampaignId(campaignId);
    try {
      const result = await communicationService.executeCampaign(campaignId);
      alert(`Campaign executed successfully! Sent: ${result.sent}, Failed: ${result.failed}`);
      await loadCampaigns();
    } catch (error) {
      console.error('Error executing campaign:', error);
      alert('Failed to execute campaign: ' + error.message);
    } finally {
      setExecutingCampaignId(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'RUNNING':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-600" />;
      case 'SCHEDULED':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-700';
      case 'FAILED':
        return 'bg-red-100 text-red-700';
      case 'RUNNING':
        return 'bg-blue-100 text-blue-700';
      case 'SCHEDULED':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (selectedCampaign) {
    return (
      <ExecutionTracking
        campaign={selectedCampaign}
        onBack={() => setSelectedCampaign(null)}
      />
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Campaigns</h3>
            <button
              onClick={handleCreateCampaign}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              + Create Campaign
            </button>
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No campaigns found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {campaigns.map(campaign => (
                <div
                  key={campaign.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900">{campaign.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(campaign.status)}`}>
                          {getStatusIcon(campaign.status)}
                          {campaign.status}
                        </span>
                      </div>
                      {campaign.communication_campaign_desc && (
                        <p className="text-sm text-gray-600 mb-2">{campaign.communication_campaign_desc}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Type: {campaign.communication_campaign_type}</span>
                        <span>•</span>
                        <span>Recipients: {campaign.total_recipients}</span>
                        <span>•</span>
                        <span>Sent: {campaign.sent_count}</span>
                        {campaign.scheduled_at && (
                          <>
                            <span>•</span>
                            <span>Scheduled: {new Date(campaign.scheduled_at).toLocaleString()}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedCampaign(campaign)}
                        className="p-2 hover:bg-gray-100 rounded transition-colors"
                        title="View executions"
                      >
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                      {campaign.status === 'DRAFT' && (
                        <button
                          onClick={() => handleExecuteCampaign(campaign.id)}
                          disabled={executingCampaignId === campaign.id}
                          className="p-2 hover:bg-blue-100 rounded transition-colors disabled:opacity-50"
                          title="Execute campaign"
                        >
                          {executingCampaignId === campaign.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                          ) : (
                            <Play className="w-4 h-4 text-blue-600" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleEditCampaign(campaign)}
                        className="p-2 hover:bg-gray-100 rounded transition-colors"
                        title="Edit campaign"
                      >
                        <Calendar className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        className="p-2 hover:bg-red-100 rounded transition-colors"
                        title="Delete campaign"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-gray-600">Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs sm:text-sm text-gray-600">per page</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-gray-600">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs sm:text-sm text-gray-700 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <CampaignModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCampaign(null);
        }}
        campaign={editingCampaign}
        onSave={handleSaveCampaign}
      />
    </>
  );
};

export default CampaignManager;

